import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import useGeolocation from '../../hooks/useGeolocation.js';
import { RADAR_PATH } from "../../config.js";

// 환경 변수로 경로 설정, 기본값 제공
const RADAR_BASE_PATH = RADAR_PATH

// 기본 날짜 설정 (fallback용)
const DEFAULT_YEAR = '2025';
const DEFAULT_MONTH = '04';
const DEFAULT_DAY = '05'; 
const DEFAULT_HOUR = '00';
const DEFAULT_MINUTE = '00';
const DEFAULT_RADAR_FILE = `rdr_${DEFAULT_YEAR}${DEFAULT_MONTH}${DEFAULT_DAY}${DEFAULT_HOUR}${DEFAULT_MINUTE}_hsp_3857.png`;

const MapContainer = ({ onCurrentPositionClick, setLatLng, initialPosition }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const imageOverlayRef = useRef(null);
  const { position, error } = useGeolocation();
  const defaultPosition = { lat: 36.5665, lng: 127.6780 }; // 한국 중심
  const zoom = 6.5;
  const [radarImages, setRadarImages] = useState([]);
  const [radarTimes, setRadarTimes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // 이미지 경계 좌표 설정
  const imageBounds = [
    [31.0, 115.0], 
    [45.0, 139.0] 
  ];

  // 이미지 존재 여부 확인 함수
  const checkImageExists = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      
      // 로딩 성공
      img.onload = () => {
        resolve(true);
      };
      
      // 로딩 실패
      img.onerror = () => {
        resolve(false);
      };
      
      // 타임아웃 설정 (3초)
      const timeout = setTimeout(() => {
        console.log(`이미지 확인 타임아웃: ${url}`);
        img.src = ""; // 로딩 중단
        resolve(false);
      }, 3000);
      
      // 이벤트 발생 시 타임아웃 취소
      img.onload = () => {
        clearTimeout(timeout);
        console.log(`이미지 확인 성공: ${url}`);
        resolve(true);
      };
      
      img.onerror = () => {
        clearTimeout(timeout);
        console.log(`이미지 확인 실패: ${url}`);
        resolve(false);
      };
      
      // 캐싱 방지
      img.src = `${url}?t=${new Date().getTime()}`;
    });
  };

  // 파일명 생성 함수
  const createFilename = (dateTime) => {
    const year = dateTime.getFullYear();
    const month = String(dateTime.getMonth() + 1).padStart(2, '0');
    const day = String(dateTime.getDate()).padStart(2, '0');
    const hours = String(dateTime.getHours()).padStart(2, '0');
    const minutes = String(dateTime.getMinutes()).padStart(2, '0');
    
    // 10분 단위로 맞추기
    const adjustedMinutes = Math.floor(minutes / 10) * 10;
    const formattedMinutes = String(adjustedMinutes).padStart(2, '0');
    
    return `rdr_${year}${month}${day}${hours}${formattedMinutes}_hsp_3857.png`;
  };

  // 레이더 이미지 불러오기
  useEffect(() => {
    const loadRadarImages = async () => {
      setIsLoading(true);
      try {
        // 실제 현재 시간 사용
        const currentDate = new Date();        
        const endTime = currentDate;
        
        const startTime = new Date(currentDate.getTime() - (3 * 60 * 60 * 1000));
        
        const timeInterval = 10;
        const imageList = [];
        const timesList = [];
        
        // 현재 시간 기준 최신 파일 확인
        const currentFilename = createFilename(currentDate);
        const currentFilePath = `${RADAR_BASE_PATH}${currentFilename}`;
        
        const currentFileExists = await checkImageExists(currentFilePath);
        
        if (currentFileExists) {
          // 현재 시간 기준으로 파일이 존재하면 현재 시간 사용
          let currentTime = new Date(startTime);
          while (currentTime <= endTime) {
            const filename = createFilename(currentTime);
            const filePath = `${RADAR_BASE_PATH}${filename}`;
            
            const hours = String(currentTime.getHours()).padStart(2, '0');
            const adjustedMinutes = Math.floor(currentTime.getMinutes() / 10) * 10;
            const formattedMinutes = String(adjustedMinutes).padStart(2, '0');
            
            imageList.push(filePath);
            timesList.push(`${hours}:${formattedMinutes}`);
            
            currentTime = new Date(currentTime.getTime() + (timeInterval * 60 * 1000));
          }
        } else {
          console.log('기본 파일 사용');
          // 기본 날짜 사용
          const defaultDate = new Date(`${DEFAULT_YEAR}-${DEFAULT_MONTH}-${DEFAULT_DAY}T${DEFAULT_HOUR}:${DEFAULT_MINUTE}:00`);
          const defaultEndTime = new Date(defaultDate);
          defaultEndTime.setHours(defaultEndTime.getHours() + 3);
          
          let defaultTime = new Date(defaultDate);
          while (defaultTime <= defaultEndTime) {
            const filename = createFilename(defaultTime);
            const filePath = `/img/rdr/${filename}`;
            
            const hours = String(defaultTime.getHours()).padStart(2, '0');
            const adjustedMinutes = Math.floor(defaultTime.getMinutes() / 10) * 10;
            const formattedMinutes = String(adjustedMinutes).padStart(2, '0');
            
            imageList.push(filePath);
            timesList.push(`${hours}:${formattedMinutes}`);
            
            defaultTime = new Date(defaultTime.getTime() + (timeInterval * 60 * 1000));
          }
        }
        
        // 이미지 목록이 비어있으면 기본 이미지 사용
        if (imageList.length === 0) {
          imageList.push(`${RADAR_BASE_PATH}${DEFAULT_RADAR_FILE}`);
          timesList.push(`${DEFAULT_HOUR}:${DEFAULT_MINUTE}`);
        }
        
        setRadarImages(imageList);
        setRadarTimes(timesList);
        
        
      } catch (error) {
        console.error("레이더 이미지 로드 실패:", error);
        
        // 오류 발생 시 기본 이미지 사용
        setRadarImages([`${RADAR_BASE_PATH}${DEFAULT_RADAR_FILE}`]);
        setRadarTimes([`${DEFAULT_HOUR}:${DEFAULT_MINUTE}`]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadRadarImages();
  }, []);

  // 지도 초기화
  useEffect(() => {
    if (!mapInstanceRef.current) {
      // 초기 위치 결정 (GPS 위치 → 초기 위치 → 기본 위치 순으로 사용)
      const startPosition = position || initialPosition || defaultPosition;
      
      const map = L.map('map', {
        zoomControl: false 
      }).setView(
        [startPosition.lat, startPosition.lng],
        zoom
      );

      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}').addTo(map);

      map.on('moveend', () => {
        const center = map.getCenter();
        setLatLng({ lat: center.lat, lng: center.lng });
      });

      mapInstanceRef.current = map;
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [initialPosition]);

  // 레이더 이미지 추가
  useEffect(() => {
    if (mapInstanceRef.current && radarImages.length > 0 && !isLoading) {
      if (imageOverlayRef.current) {
        imageOverlayRef.current.remove();
      }
      
      imageOverlayRef.current = L.imageOverlay(radarImages[0], imageBounds, {
        opacity: 0.5,
        interactive: false
      }).addTo(mapInstanceRef.current);
      
      window.radarControl = {
        updateRadarImage: (index) => {
          if (!mapInstanceRef.current || !imageOverlayRef.current) return;
          
          if (imageOverlayRef.current) {
            imageOverlayRef.current.remove();
          }
          
          imageOverlayRef.current = L.imageOverlay(radarImages[index], imageBounds, {
            opacity: 0.5,
            interactive: false
          }).addTo(mapInstanceRef.current);
        },
        radarImages,
        radarTimes,
        totalImages: radarImages.length
      };
    }
  }, [radarImages, isLoading]);

  useEffect(() => {
    if (mapInstanceRef.current && position) {
      onCurrentPositionClick(() => {
        mapInstanceRef.current.setView([position.lat, position.lng], zoom);
      });
    }
  }, [position, onCurrentPositionClick]);

  const containerStyle = {
    width: '330px',
    height: '490px',
    borderRadius: '1.5rem',
    position: 'relative'
  };

  return (
    <div style={{ position: 'relative' }}>
      <div id="map" style={containerStyle}>
        {isLoading && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(255, 255, 255, 0.7)',
            padding: '10px',
            borderRadius: '5px',
            zIndex: 1000
          }}>
            레이더 이미지 로딩 중...
          </div>
        )}
      </div>
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '20px',
        height: '20px',
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
        pointerEvents: 'none',
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#333'
      }}>+</div>
    </div>
  );
};

export default MapContainer;